import { CodeBlock, StepItem, AlertBox, Screenshot, BulletList } from '../components/UIComponents';
import {
    ClipboardList,
    Wrench,
    Monitor,
    Globe2,
    Package,
    Settings,
    Link2,
    RefreshCw,
    CheckCircle2,
    Globe,
    BarChart3,
    Server,
    Laptop,
    Key,
    Database,
    Cpu,
    ShieldCheck,
    DoorOpen,
    Network,
    Zap,
    Activity,
    Box,
    RadioTower
} from 'lucide-react';

interface AgentInstallationSectionProps {
    activeMethod: 'online' | 'offline';
    setActiveMethod: (method: 'online' | 'offline') => void;
}

export function AgentInstallationSection({ activeMethod, setActiveMethod }: AgentInstallationSectionProps) {

    return (
        <div className="space-y-8">
            {/* ── Overview ── */}
            <section className="doc-section scroll-mt-20" id="overview">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <ClipboardList className="text-blue-500" size={22} />
                        Overview
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Ovul Scanner is a comprehensive network scanning and monitoring platform. This SOP covers
                        the installation and configuration of its agents, credential management, and scan scheduling.
                    </p>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-4 md:p-5 hover:border-orange-500/30 hover:shadow-[0_0_20px_rgba(249,115,22,0.1)] transition-all">
                    <div className="text-[14px] font-bold text-gray-100 mb-1.5 flex items-center gap-2">
                        <ClipboardList size={16} className="text-blue-400" />
                        About This Document
                    </div>
                    <div className="text-[13px] text-gray-200 leading-relaxed">
                        This Standard Operating Procedure provides step-by-step instructions for installing
                        the Ovul Scanner agents, configuring camera, SNMP, and firewall credentials,
                        and setting up automated scan schedules. Follow the sections in order for a complete setup,
                        or use the sidebar to jump to specific topics.
                    </div>
                </div>
            </section>

            {/* ── Agent Types ── */}
            <section className="doc-section scroll-mt-20" id="agent-types">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Wrench className="text-orange-500" size={22} />
                        Agent Types
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Ovul Scanner supports two types of agents for different deployment scenarios.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                    <div className="group bg-gray-800 border border-white/5 rounded-xl p-5 relative overflow-hidden transition-all hover:-translate-y-0.5 hover:border-orange-500/40 hover:shadow-xl hover:shadow-orange-500/5">
                        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-orange-600 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-3 text-orange-400">
                            <Globe2 size={20} />
                        </div>
                        <h3 className="text-[15px] font-bold text-gray-100 mb-1.5">1. Master Gateway Agent</h3>
                        <p className="text-[13px] text-gray-200 leading-relaxed">
                            The Master Gateway Agent serves as a centralized communication gateway
                            between the Ovul Scanner Server and Endpoint Agents deployed in isolated
                            or restricted networks. It enables secure monitoring, discovery,
                            authenticated scanning, and infrastructure visibility where direct
                            connectivity to the central server is not possible.
                        </p>
                    </div>
                    <div className="group bg-gray-800 border border-white/5 rounded-xl p-5 relative overflow-hidden transition-all hover:-translate-y-0.5 hover:border-orange-500/40 hover:shadow-xl hover:shadow-orange-500/5">
                        <div className="absolute top-0 left-0 right-0 h-[2px] bg-gradient-to-r from-orange-600 to-orange-400 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                        <div className="w-10 h-10 bg-orange-500/10 rounded-lg flex items-center justify-center mb-3 text-orange-400">
                            <Laptop size={20} />
                        </div>
                        <h3 className="text-[15px] font-bold text-gray-100 mb-1.5">2. End Point agent</h3>
                        <p className="text-[13px] text-gray-200 leading-relaxed">
                            Installed directly on endpoint systems and assets for monitoring, scanning,
                            and communication with the Ovul Scanner platform. Supports both online and
                            offline installation modes.
                        </p>
                    </div>
                </div>
            </section>

            {/* ── Platforms ── */}
            <section className="doc-section scroll-mt-20" id="platforms">
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Monitor className="text-purple-500" size={22} />
                        Supported Platforms
                    </h2>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-3">End Point agent Platforms</div>
                        <div className="flex flex-wrap gap-2.5 mb-4">
                            {[
                                { name: 'Windows', icon: '/windows.png' },
                                { name: 'Linux', icon: '/linux.svg' },
                                { name: 'macOS', icon: '/mac.png' },
                                { name: 'ARM Architecture', icon: '/arm.svg' }
                            ].map(platform => (
                                <div key={platform.name} className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/80 border border-white/5 rounded-lg shadow-sm hover:border-orange-500/30 hover:bg-black/40 transition-colors cursor-default">
                                    <div className="w-6 h-6 bg-white/5 rounded flex items-center justify-center p-0.5">
                                        <img src={platform.icon} alt={platform.name} className="w-full h-full object-contain" />
                                    </div>
                                    <span className="text-[13px] font-semibold text-gray-200 tracking-wide">{platform.name}</span>
                                </div>
                            ))}
                        </div>
                        <AlertBox type="note" icon={<CheckCircle2 size={16} />}>
                            The End Point agent can be installed in both <strong>Online Mode</strong> and <strong>Offline Mode</strong>.
                        </AlertBox>
                    </div>
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-3 flex items-center gap-2">
                            <RadioTower className="text-orange-400" size={16} />
                            Master Gateway Agent Platforms
                        </div>
                        <div className="flex flex-wrap gap-2.5 mb-4">
                            {[
                                { name: 'Linux', icon: '/linux.svg' },
                                { name: 'ARM Architecture', icon: '/arm.svg' }
                            ].map(platform => (
                                <div key={platform.name} className="flex items-center gap-2 px-3 py-1.5 bg-gray-900/80 border border-white/5 rounded-lg shadow-sm hover:border-orange-500/30 hover:bg-black/40 transition-colors cursor-default">
                                    <div className="w-6 h-6 bg-white/5 rounded flex items-center justify-center p-0.5">
                                        <img src={platform.icon} alt={platform.name} className="w-full h-full object-contain" />
                                    </div>
                                    <span className="text-[13px] font-semibold text-gray-200 tracking-wide">{platform.name}</span>
                                </div>
                            ))}
                            {[
                                { name: 'Windows', icon: '/windows.png' },
                                { name: 'macOS', icon: '/mac.png' }
                            ].map(platform => (
                                <div key={platform.name} className="flex items-center gap-2 px-3 py-1.5 bg-red-500/5 border border-red-500/10 rounded-lg shadow-sm opacity-60 grayscale cursor-not-allowed group relative overflow-hidden">
                                    <div className="absolute top-1/2 left-2 right-2 h-[1px] bg-red-500/70 -translate-y-1/2 pointer-events-none shadow-[0_0_2px_rgba(239,68,68,0.5)]"></div>
                                    <div className="w-6 h-6 bg-white/5 rounded flex items-center justify-center p-0.5">
                                        <img src={platform.icon} alt={platform.name} className="w-full h-full object-contain opacity-50" />
                                    </div>
                                    <span className="text-[13px] font-semibold text-red-200/80 tracking-wide">{platform.name}</span>
                                </div>
                            ))}
                        </div>
                        <AlertBox type="warning" icon={<ShieldCheck size={16} />}>
                            The Master Gateway Agent is <strong>not</strong> supported on Windows or macOS.
                        </AlertBox>
                    </div>
                </div >
            </section >

            {/* ── Master Gateway ── */}
            < section className="doc-section scroll-mt-20" id="master-gateway" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Globe2 className="text-cyan-500" size={22} />
                        How the Master Gateway Agent Works
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        The Master Gateway Agent functions as a communication bridge for all Offline End Point
                        Agents installed within the same network environment.
                    </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-1 gap-2">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-2.5 flex items-center gap-2">
                            <Key size={16} className="text-cyan-400" /> Key Functions
                        </div>
                        <BulletList items={[
                            'Network Discovery: Automatically discovers devices, hosts, switches, routers, servers, endpoints, and connected assets within the network. Supports subnet-based, IP-range, and credential-assisted discovery methods.',
                            'SNMP Discovery & Monitoring: Performs SNMP-based discovery for network devices and infrastructure components. Collects device information, interface details, bandwidth usage, health metrics, and operational status.',
                            'Firewall Scanning & Compliance Reporting: Detects and analyzes firewall configurations, open ports, active services, exposed interfaces, and security rules. Identifies misconfigurations, unauthorized access points, and potential security vulnerabilities.',
                            'Camera Monitoring & Streaming: Discovers IP cameras and surveillance devices within the network. Enables device health monitoring, status tracking, and live stream integration where supported.Receives scheduled jobs and scan configurations from the server',
                            'iLO / iDRAC Integration: Connects with server management interfaces such as HP iLO and Dell iDRAC. Retrieves hardware health, remote management information, power status, temperature metrics, and system alerts.',
                            'Topology Mapping: Automatically generates visual network topology maps. Displays relationships and connectivity between switches, routers, firewalls, servers, endpoints, and other network assets.',
                            'Credential-Based & Authenticated Scanning: Uses secure credentials and authenticated access methods for deep infrastructure scanning. Provides enhanced visibility into operating systems, services, configurations, vulnerabilities, patches, and hardware inventory.',
                            'Isolated Network Communication Middleware: In isolated or restricted environments, the Master Gateway Agent acts as a secure communication interface/middleware between Endpoint Agents and the Central Ovul Server. Enables secure synchronization of scan data, alerts, monitoring information, and device intelligence without directly exposing the isolated network to external connectivity.',
                        ]} />
                    </div>
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-2.5 flex items-center gap-2">
                            <Zap size={16} className="text-amber-400" /> Benefits
                        </div>
                        <BulletList items={[
                            'Designed for isolated, segmented, and air-gapped environments',
                            'Centralized visibility across distributed infrastructure',
                            'Secure communication bridge between isolated networks and the central server',
                            'Enhanced infrastructure monitoring and asset visibility',
                            'Improved compliance and audit readiness',
                            'Scalable deployment across enterprise and industrial environments',
                            'Reduced operational risk through authenticated and controlled scanning'
                        ]} />
                    </div>
                </div>
            </section >

            {/* ── Master Prerequisites ── */}
            < section className="doc-section scroll-mt-20" id="master-prereqs" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Package className="text-amber-500" size={22} />
                        Prerequisites for Master Gateway Agent
                    </h2>
                    <p className="text-[14px] text-gray-200 mt-1.5 leading-relaxed">
                        Ensure the following requirements are met before installing the Master Gateway Agent on an ARM-based Linux device.
                    </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-2.5">System Requirements</div>
                        <BulletList items={[
                            'ARM-based Linux Device',
                            'Ubuntu/Debian-based Operating System',
                            'Root or sudo access',
                            'Internet connectivity',
                            'Minimum required system packages installed'
                        ]} />
                    </div>

                    <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                        <div className="text-[14px] font-bold text-gray-100 mb-2.5">Required Dependencies</div>
                        <CodeBlock
                            label="Install Dependencies (Terminal)"
                            commands={[
                                'sudo apt update',
                                'sudo apt install nmap tcpdump snmp -y',
                            ]}
                        />
                        <div className="mt-3 rounded-lg border border-white/10 overflow-hidden text-[12px]">
                            <table className="w-full text-left border-collapse">
                                <thead className="bg-gray-800/80">
                                    <tr>
                                        <th className="px-4 py-2 font-bold uppercase tracking-wider text-gray-200 border-b border-white/10">Package</th>
                                        <th className="px-4 py-2 font-bold uppercase tracking-wider text-gray-200 border-b border-white/10">Purpose</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-gray-900/40">
                                    <tr className="hover:bg-white/5 border-b border-white/5"><td className="px-4 py-2 text-gray-300"><strong>nmap</strong></td><td className="px-4 py-2 text-gray-200">Network discovery and scanning</td></tr>
                                    <tr className="hover:bg-white/5 border-b border-white/5"><td className="px-4 py-2 text-gray-300"><strong>tcpdump</strong></td><td className="px-4 py-2 text-gray-200">Packet capture and network troubleshooting</td></tr>
                                    <tr className="hover:bg-white/5"><td className="px-4 py-2 text-gray-300"><strong>snmp</strong></td><td className="px-4 py-2 text-gray-200">SNMP communication and monitoring support</td></tr>
                                </tbody>
                            </table>
                        </div>
                    </div>


                </div>
                <AlertBox type="warning" icon={<ShieldCheck size={16} />}>
                    Ensure all dependencies are installed <strong>before</strong> running the Master Gateway Agent installation script.
                    The device must have proper network access to communicate.
                </AlertBox>
            </section >

            {/* ── Master Gateway Install Steps ── */}
            < section className="doc-section scroll-mt-20" id="master-install" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Settings className="text-emerald-500" size={22} />
                        Master Gateway Agent Installation
                    </h2>
                </div>
                <div className="group space-y-4">
                    <StepItem num={1} title="Login to the Ovul Scanner Portal">
                        Open the Ovul Scanner Portal in your browser. Enter your login credentials and click <strong className="text-gray-200">Login</strong>.
                        <Screenshot src="/login.png" alt="Login to Ovul Scanner Portal" />
                    </StepItem>
                    <StepItem num={2} title="Navigate to Agents Section">
                        After login, go to the <strong className="text-gray-200">Company Level Dashboard</strong>. Click on the <strong className="text-gray-200">Agents</strong> menu from the left navigation panel.
                        <Screenshot src="/Agent_secation.png" alt="Navigate to Agents Section" />
                    </StepItem>
                    <StepItem num={3} title="Open Installation Window">
                        In the Agents page, click the <strong className="text-gray-200">Install</strong> icon located at the top-right corner.
                        <Screenshot src="/Open_Installation_Window.png" alt="Open Installation Window" />
                    </StepItem>
                    <StepItem num={4} title="Select Operating System">
                        Choose the appropriate Operating System for the target machine:
                        <BulletList items={['Linux', 'ARM']} />
                        <Screenshot src="/Select_Operating_System.png" alt="Select Operating System" />
                    </StepItem>
                    <StepItem num={5} title="Select Agent Type">
                        In the Agent Type section, select <strong className="text-gray-200">Master Gateway Agent</strong>.
                    </StepItem>
                    <StepItem num={6} title="Copy Installation Script">
                        The portal will generate the installation script automatically. Click on <strong className="text-gray-200">Copy Script</strong>.
                    </StepItem>
                    <StepItem num={7} title="Connect to ARM Machine via SSH">
                        <CodeBlock label="SSH Command" commands={['ssh username@<server-ip-address>']} />
                        <Screenshot src="/Connect_to_the_ARM_Machine_Using_SSH.png" alt="Connect to the ARM Machine Using SSH" />
                    </StepItem>
                    <StepItem num={8} title="Execute Installation Script">
                        Paste the copied installation script into the terminal. Press <strong className="text-gray-200">Enter</strong> and wait for installation to complete.
                    </StepItem>
                    <StepItem num={9} title="Verify Agent Installation">
                        Return to the Ovul Scanner Portal. Navigate to the <strong className="text-gray-200">Agents</strong> section and verify the Master Gateway Agent is listed and connected.
                        <Screenshot src="/Verify_Agent_Installation.png" alt="Verify Agent Installation" />
                    </StepItem>
                    <StepItem num={10} title="Verify Agent Status">
                        Go to the <strong className="text-gray-200">Agents</strong> page and verify the Master Gateway Agent appears with status <strong className="text-gray-200">Online</strong>.
                    </StepItem>
                    <StepItem num={11} title="Verify Asset Information">
                        <div className="space-y-3">
                            <p className="text-[13.5px] text-gray-200 leading-relaxed">
                                Once the Master Gateway Agent is installed, it will automatically start the <strong className="text-gray-200">Network Discovery</strong> process.
                            </p>

                            <div className="bg-orange-500/5 border border-orange-500/10 rounded-lg p-3">
                                <div className="text-[12px] font-bold text-orange-400 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                                    <Activity size={14} /> Discovery Duration
                                </div>
                                <p className="text-[13px] text-gray-200 mb-2">The initial discovery duration depends on network size and the number of assets available in the subnet.</p>
                                <BulletList items={[
                                    <span><strong>Example:</strong> In a /24 network (~254 assets), the first discovery cycle may take 15 to 25 minutes to complete.</span>
                                ]} />
                            </div>

                            <div className="bg-blue-500/5 border border-blue-500/10 rounded-lg p-3">
                                <div className="text-[12px] font-bold text-blue-400 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                                    <RefreshCw size={14} /> During Discovery
                                </div>
                                <BulletList items={[
                                    'Assets are identified automatically',
                                    'Devices are scanned one by one',
                                    'Asset information is posted to the Central Ovul Portal progressively'
                                ]} />
                            </div>

                            <div className="bg-emerald-500/5 border border-emerald-500/10 rounded-lg p-3">
                                <div className="text-[12px] font-bold text-emerald-400 mb-2 uppercase tracking-wider flex items-center gap-1.5">
                                    <CheckCircle2 size={14} /> Post-Discovery
                                </div>
                                <BulletList items={[
                                    'Navigate to the Assets page in the portal',
                                    'Verify the discovered assets and device information'
                                ]} />
                            </div>
                            <Screenshot src="/Verify_Asset_Information.png" alt="Verify Asset Information" />
                        </div>
                    </StepItem>
                </div>
            </section >

            {/* ── Endpoint Agents ── */}
            < section className="doc-section scroll-mt-20" id="endpoint-install" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Link2 className="text-indigo-500" size={22} />
                        Install Endpoint Agents via Master Gateway
                    </h2>
                </div>
                <div className="group space-y-4">
                    <StepItem num={1} title="Open Agent Installation">
                        Go to the <strong className="text-gray-200">Agents</strong> section and click on <strong className="text-gray-200">Install Agent</strong>.
                    </StepItem>
                    <StepItem num={2} title="Select OS and Agent Type">
                        Select the required Operating System, then select <strong className="text-gray-200">End Point agent</strong>.
                    </StepItem>
                    <StepItem num={3} title="Select Master Gateway Agent">
                        A <strong className="text-gray-200">Master Agent Dropdown</strong> will appear. Select the required Master Gateway Agent from the dropdown list.
                    </StepItem>
                </div>
                <AlertBox type="success" icon={<CheckCircle2 size={16} />}>
                    The Endpoint Agent will now communicate through the selected Master Gateway Agent.
                </AlertBox>
            </section >

            {/* ── Communication Flow ── */}
            < section className="doc-section scroll-mt-20" id="comm-flow" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <RefreshCw className="text-rose-500" size={22} />
                        Communication Flow
                    </h2>
                </div>
                <div className="relative flex flex-col md:flex-row items-center justify-between gap-6 md:gap-4 my-8 py-8 px-6 rounded-2xl bg-gradient-to-b from-gray-900/80 to-black/60 border border-white/5 shadow-2xl shadow-black/40 overflow-hidden group">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-orange-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-1000 blur-2xl"></div>

                    <div className="relative z-10 flex flex-col items-center gap-3 w-full md:w-1/3">
                        <div className="w-14 h-14 rounded-full bg-gray-800/80 border border-gray-600 flex items-center justify-center shadow-lg transition-all duration-300 group-hover:scale-110 group-hover:border-gray-400 group-hover:bg-gray-700">
                            <Laptop size={24} className="text-gray-300" />
                        </div>
                        <div className="text-center mt-1">
                            <div className="text-[14px] font-bold text-gray-200">Endpoint Agents</div>
                            <div className="text-[11px] text-gray-200 mt-0.5 uppercase tracking-widest font-semibold">Local Network</div>
                        </div>
                    </div>

                    <div className="relative z-10 flex flex-col items-center shrink-0">
                        <div className="h-10 md:h-0.5 w-0.5 md:w-16 bg-gradient-to-b md:bg-gradient-to-r from-gray-700 to-orange-500/50"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-900 border border-white/10 rounded-full p-1.5 shadow-md">
                            <RefreshCw size={12} className="text-gray-200 opacity-60 group-hover:text-orange-400 group-hover:animate-pulse transition-colors" />
                        </div>
                    </div>

                    <div className="relative z-10 flex flex-col items-center gap-3 w-full md:w-1/3">
                        <div className="w-16 h-16 rounded-full bg-orange-500/10 border-2 border-orange-500/50 flex items-center justify-center shadow-[0_0_15px_rgba(249,115,22,0.15)] transition-all duration-300 group-hover:scale-110 group-hover:shadow-[0_0_25px_rgba(249,115,22,0.3)] group-hover:bg-orange-500/20">
                            <Globe2 size={28} className="text-orange-400" />
                        </div>
                        <div className="text-center mt-1 flex flex-col items-center">
                            <div className="text-[15px] font-bold text-orange-400">Master Gateway</div>
                            <div className="text-[11px] text-orange-500/70 mt-0.5 uppercase tracking-widest font-semibold mb-2">Secure Bridge</div>
                            <div className="flex gap-2 border-t border-orange-500/20 pt-3">
                                <div className="w-8 h-8 bg-black/40 rounded shadow-md flex items-center justify-center p-1.5 border border-orange-500/30 hover:bg-orange-500/20 transition-all hover:scale-110" title="Linux Supported">
                                    <img src="/linux.svg" alt="Linux" className="w-full h-full object-contain drop-shadow" />
                                </div>
                                <div className="w-8 h-8 bg-black/40 rounded shadow-md flex items-center justify-center p-1.5 border border-orange-500/30 hover:bg-orange-500/20 transition-all hover:scale-110" title="ARM Architecture Supported">
                                    <img src="/arm.svg" alt="ARM Architecture" className="w-full h-full object-contain drop-shadow" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="relative z-10 flex flex-col items-center shrink-0">
                        <div className="h-10 md:h-0.5 w-0.5 md:w-16 bg-gradient-to-b md:bg-gradient-to-r from-orange-500/50 to-gray-700"></div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-gray-900 border border-white/10 rounded-full p-1.5 shadow-md">
                            <RefreshCw size={12} className="text-gray-200 opacity-60 group-hover:text-orange-400 group-hover:animate-pulse transition-colors" />
                        </div>
                    </div>

                    <div className="relative z-10 flex flex-col items-center gap-3 w-full md:w-1/3">
                        <div>
                            <img src="/owllogopng.png" alt="Ovul Server" className="w-14 h-14 object-contain mt-1" />
                        </div>
                        <div className="text-center mt-1">
                            <div className="text-[14px] font-bold text-gray-200">Ovul Server</div>
                            <div className="text-[11px] text-gray-200 mt-0.5 uppercase tracking-widest font-semibold">Cloud Platform</div>
                        </div>
                    </div>
                </div>
                <AlertBox type="note" icon={<CheckCircle2 size={16} />}>
                    Scan jobs, schedules, and monitoring data are synchronized automatically between all agents and the server.
                </AlertBox>
            </section >

            {/* ── Lightweight Prerequisites ── */}
            < section className="doc-section scroll-mt-20" id="lw-prereqs" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <CheckCircle2 className="text-green-500" size={22} />
                        End Point agent Prerequisites
                    </h2>
                </div>
                <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-xl p-5">
                    <BulletList items={[
                        'Allow outbound access on port 443 (HTTPS) from the endpoint/server',
                        'Whitelist the domain: *.ovulscanner.com',
                    ]} />
                </div>
            </section >

            {/* ── Online / Offline Installation ── */}
            < section className="doc-section scroll-mt-20" id="lw-online" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <Globe className="text-sky-500" size={22} />
                        End Point agent Installation
                    </h2>
                </div>
                <div className="flex gap-1 mb-5 bg-gray-800 p-1 rounded-lg border border-white/10">
                    <button
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-[12px] font-bold transition-all ${activeMethod === 'online' ? 'bg-orange-500 text-white shadow-md' : 'text-gray-200 hover:text-gray-200 hover:bg-white/5'}`}
                        onClick={() => setActiveMethod('online')}
                    >
                        <Globe size={14} /> Online
                    </button>
                    <button
                        className={`flex-1 flex items-center justify-center gap-2 py-2 px-4 rounded-md text-[12px] font-bold transition-all ${activeMethod === 'offline' ? 'bg-orange-500 text-white shadow-md' : 'text-gray-200 hover:text-gray-200 hover:bg-white/5'}`}
                        onClick={() => setActiveMethod('offline')}
                    >
                        <Link2 size={14} /> Offline
                    </button>
                </div>

                {
                    activeMethod === 'online' && (
                        <div className="flex flex-col gap-4 group animate-in fade-in slide-in-from-bottom-2 duration-200">
                            <StepItem num={1} title="Login to the Ovul Scanner Portal">
                                Open the portal, enter credentials, and click <strong className="text-gray-200">Login</strong>.
                                <Screenshot src="/Login_to_the_Ovul_Scanner_Portal.png" alt="Login to Ovul Scanner Portal" />
                            </StepItem>
                            <StepItem num={2} title="Navigate to Agents Section">
                                Go to the <strong className="text-gray-200">Company Level Dashboard</strong> → <strong className="text-gray-200">Agents</strong> menu.
                                <Screenshot src="/Agent_secation.png" alt="Navigate to Agents Section" />
                            </StepItem>
                            <StepItem num={3} title="Open Installation Window">
                                Click the <strong className="text-gray-200">Install</strong> icon at the top-right corner of the Agents page.
                                <Screenshot src="/Open_Installation_Window.png" alt="Open Installation Window" />
                            </StepItem>
                            <StepItem num={4} title="Select Operating System">
                                Choose the target OS:
                                <BulletList items={['Windows', 'Linux', 'macOS', 'ARM']} />
                                <Screenshot src="/Select_Operating_System.png" alt="Select Operating System" />
                            </StepItem>
                            <StepItem num={5} title="Select Agent Type">
                                Select <strong className="text-gray-200">End Point agent</strong> as the Agent Type.
                                <Screenshot src="/Select_Agent_Type.png" alt="Select Agent Type" />
                            </StepItem>
                            <StepItem num={6} title="Copy Installation Script">
                                The portal generates the script. Click <strong className="text-gray-200">Copy Script</strong>.
                            </StepItem>
                            <StepItem num={7} title="Open PowerShell as Administrator">
                                Go to Windows Search Bar → Search <strong className="text-gray-200">PowerShell</strong> → Right-click → <strong className="text-gray-200">Run as Administrator</strong>.
                                <Screenshot src="/Open_PowerShell_as_Administrator.png" alt="Open PowerShell as Administrator" />
                            </StepItem>
                            <StepItem num={8} title="Execute Installation Script">
                                Paste the copied script into PowerShell and press <strong className="text-gray-200">Enter</strong>. Wait for the process to complete.
                                <Screenshot src="/Execute_Installation_Script.png" alt="Execute Installation Script" />
                            </StepItem>
                            <StepItem num={9} title="Verify Agent Installation">
                                The agent will automatically communicate with the server. Refresh the <strong className="text-gray-200">Agents</strong> page and verify the system appears.
                                <Screenshot src="/Verify_Agent_Installation.png" alt="Verify Agent Installation" />
                            </StepItem>
                        </div>
                    )
                }

                {
                    activeMethod === 'offline' && (
                        <div className="flex flex-col gap-4 group animate-in fade-in slide-in-from-bottom-2 duration-200">
                            <StepItem num={1} title="Download Offline Installer">
                                Log in to the portal → <strong className="text-gray-200">Agents</strong> → <strong className="text-gray-200">Install</strong> icon → <strong className="text-gray-200">Select OS</strong> → Choose <strong className="text-gray-200">End Point agent</strong>.
                                <Screenshot src="/Download_Offline_Installer.png" alt="Download Offline Installer" />
                            </StepItem>
                            <StepItem num={2} title="Select Master Gateway Agent">
                                <p>
                                    After selecting the Operating System (OS), choose the <strong className="text-gray-200">Master Gateway Agent</strong> during the Offline Endpoint Agent installation process.
                                </p>
                                <p>
                                    The selected Master Gateway Agent will act as the communication bridge between the Offline Endpoint Agent and the Central Ovul Server. Then select the <strong className="text-gray-200">offline</strong> option and <strong className="text-gray-200">Download the installer</strong>.
                                </p>
                                <Screenshot src="/Select_Master_Gateway_Agent.jpeg" alt="Select Master Gateway Agent" />
                            </StepItem>
                            <StepItem num={3} title="Run Installer as Administrator">
                                Right-click the installer file and select <strong className="text-gray-200">Run as Administrator</strong>.
                            </StepItem>
                            <StepItem num={4} title="Complete Installation">
                                Follow the installation wizard. Accept permissions, click <strong className="text-gray-200">Install</strong>, and wait for completion.
                            </StepItem>
                            <StepItem num={5} title="Verify Agent Status">
                                Open the portal → <strong className="text-gray-200">Agents</strong> → Confirm the system is listed and connected.
                                <Screenshot src="/Verify_Agent_Installation.png" alt="Verify Agent Status" />
                            </StepItem>
                            <StepItem num={6} title="Verify Asset Information">
                                Go to <strong className="text-gray-200">Assets</strong> and verify the asset is displayed as <strong className="text-gray-200">Online</strong>.
                                <Screenshot src="/Verify_Asset_Information.png" alt="Verify Asset Information" />
                            </StepItem>
                            <StepItem num={7} title="View Asset Details">
                                Click on the <strong className="text-gray-200">Asset Name</strong> to open the details page.
                                <Screenshot src="/View_Asset_Details.png" alt="View Asset Details" />
                            </StepItem>
                            <StepItem num={8} title="Monitor Asset Data">
                                Review collected data and ensure the asset is actively communicating with the platform.
                                <Screenshot src="/Monitor_Asset_Data.png" alt="Monitor Asset Data" />
                            </StepItem>
                        </div>
                    )
                }
            </section >

            {/* ── Asset Details ── */}
            < section className="doc-section scroll-mt-20" id="asset-details" >
                <div className="mb-3 pb-2 border-b border-white/10">
                    <h2 className="text-xl font-bold flex items-center gap-2.5 text-gray-100">
                        <BarChart3 className="text-violet-500" size={22} />
                        Asset Details & Monitoring
                    </h2>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4 mt-3 gap-3">
                    {[
                        { icon: <Key size={14} />, title: 'License Info' },
                        { icon: <Database size={14} />, title: 'RAID Details' },
                        { icon: <Box size={14} />, title: 'Software' },
                        { icon: <Activity size={14} />, title: 'Metrics' },
                        { icon: <Cpu size={14} />, title: 'Memory' },
                        { icon: <ShieldCheck size={14} />, title: 'Security' },
                        { icon: <DoorOpen size={14} />, title: 'Open Ports' },
                        { icon: <Server size={14} />, title: 'Hardware' },
                        { icon: <Network size={14} />, title: 'Network' },
                        { icon: <Zap size={14} />, title: 'Services' },
                        { icon: <CheckCircle2 size={14} />, title: 'Health' },
                    ].map((item) => (
                        <div className="bg-gradient-to-br from-gray-800 to-gray-800/50 border border-white/5 rounded-lg p-3 hover:border-orange-500/30 transition-all hover:bg-orange-500/[0.02]" key={item.title}>
                            <div className="text-[12.5px] font-bold text-gray-200 flex items-center gap-2">
                                <span className="text-violet-400">{item.icon}</span> {item.title}
                            </div>
                        </div>
                    ))}
                </div>
            </section >
        </div >
    );
}
